import React, {useCallback, useContext, useEffect, useMemo, useRef} from 'react';

import {DateRangeInput, FormInput, NumberInput} from 'src/components/input';
import {WidthItem} from 'src/components/width-item';
import {CONTRACT_ID_EXPLANATION} from 'src/constants';
import {CustomValidatedControl, ValidatedControl, ValidationContext} from 'src/contexts/validation-context';
import {useDateTime} from 'src/hooks/date-time/useDateTime';
import {
    END_BEFORE_START,
    END_REQUIRED,
    LICENSE_VALIDATIONS,
    START_REQUIRED,
} from 'src/modules/create-account/steps/license-create/components/utils';
import {CONCURRENT_USERS_REQUIRED} from 'src/modules/create-account/steps/license-create/constants';
import {SEATS_RANGE_LICENSES} from 'src/modules/profile/account/constants';
import {LicenseType as LicenseTypes} from 'src/openapi/admin/models/LicenseType';
import {SEATS_RANGE_OPTIONS, getSeatsRangeValue} from 'src/utils/license-util';

import Button from '@pb/kit-component-button';
import ComboBox, {ComboboxItem} from '@pb/kit-component-combo-box';
import {DialogFooter, DialogHeader, DialogMain} from '@pb/kit-component-dialog-v2';
import {Integer} from '@pb/kit-component-formatters';
import {Group} from '@pb/kit-component-group';
import {Info} from '@pb/kit-component-info';
import Label from '@pb/kit-component-label';
import {Message} from '@pb/kit-component-message';
import {LocaleContext} from '@pb/kit-context-locale';

import type {ValidationContextType} from 'src/contexts/validation-context';
import type {LicenseInfo} from 'src/openapi/admin/models/LicenseInfo';

const hasOwn = (object: object, key: PropertyKey) => Object.prototype.hasOwnProperty.call(object, key);

const sanitizeSeatValue = (value?: number | null): number | null | undefined => {
    if (typeof value === 'number' && !Number.isNaN(value)) {
        return value;
    }
    if (value === null) {
        return null;
    }
    return undefined;
};

type Props = {
    busy: boolean,
    license: LicenseInfo,
    isInitialLicenseInformation: boolean,
    onClose: () => void,
    onChange: React.Dispatch<React.SetStateAction<LicenseInfo | null>>,
    onSave: () => void,
};

export const EditLicenseInformationModal: React.FC<Props> = props => {
    const {onClose, license, isInitialLicenseInformation, onChange, onSave, busy} = props;
    const {addError, removeError, validationErrors} = useContext<ValidationContextType>(ValidationContext);
    const {isAfterDate, isBeforeToday} = useDateTime();
    const {locale} = useContext(LocaleContext);
    const initialSeatsRawRef = useRef<LicenseInfo['concurrentUserCount']>(license.concurrentUserCount);
    const initialHasSeatsRef = useRef(hasOwn(license, 'concurrentUserCount'));

    useEffect(() => {
        if (isInitialLicenseInformation) {
            initialSeatsRawRef.current = license.concurrentUserCount;
            initialHasSeatsRef.current = hasOwn(license, 'concurrentUserCount');
        }
    }, [isInitialLicenseInformation, license.concurrentUserCount, license]);

    useEffect(() => {
        const {startDate, endDate} = license;
        /* TODO: use full license period validation when Deactivate license will be enabled */

        if (!startDate) {
            addError({
                code: START_REQUIRED,
                controlId: 'license-start-date',
                message: LICENSE_VALIDATIONS[START_REQUIRED],
            });
        } else {
            removeError('license-start-date', [START_REQUIRED]);
        }

        if (!endDate) {
            addError({
                code: END_REQUIRED,
                controlId: 'license-end-date',
                message: LICENSE_VALIDATIONS[END_REQUIRED],
            });
        } else {
            removeError('license-end-date', [END_REQUIRED]);
        }

        if (startDate && endDate && isAfterDate(startDate, endDate) && startDate !== endDate) {
            addError({
                code: END_BEFORE_START,
                controlId: 'license-end-date',
                message: LICENSE_VALIDATIONS[END_BEFORE_START],
            });
        } else {
            removeError('license-end-date', [END_BEFORE_START]);
        }

        if (license.typeCode === LicenseTypes.CU && !license.concurrentUserCount) {
            addError({
                code: 'CONCURRENT_USERS_REQUIRED',
                controlId: 'license-seats',
                message: CONCURRENT_USERS_REQUIRED,
            });
        } else {
            removeError('license-seats');
        }
    }, [license, isAfterDate, addError, removeError]);

    const onChangeStartDate = useCallback((startDate: string) => {
        onChange(prevState => (prevState ? {...prevState, startDate} : null));
    }, [onChange]);

    const onChangeEndDate = useCallback((endDate: string) => {
        onChange(prevState => (prevState ? {...prevState, endDate} : null));
    }, [onChange]);

    const onChangeSeats = useCallback((nextCount?: number | null) => {
        onChange(prevState => {
            if (!prevState) {
                return null;
            }

            const nextValue = sanitizeSeatValue(nextCount);
            const prevValue = sanitizeSeatValue(prevState.concurrentUserCount);
            const prevHasValue = hasOwn(prevState, 'concurrentUserCount');

            if (prevValue === nextValue) {
                if (nextValue === undefined && !prevHasValue) {
                    return prevState;
                }
                if (typeof nextValue === 'number' && prevState.concurrentUserCount === nextValue) {
                    return prevState;
                }
            }

            if (nextValue === undefined) {
                const initialHasValue = initialHasSeatsRef.current;
                const initialRawValue = initialSeatsRawRef.current;

                if (!initialHasValue) {
                    if (!prevHasValue) {
                        return prevState;
                    }
                    const {concurrentUserCount: _discard, ...rest} = prevState;
                    return rest as LicenseInfo;
                }

                return {...prevState, concurrentUserCount: initialRawValue};
            }

            if (nextValue === null && prevState.concurrentUserCount === null) {
                return prevState;
            }

            if (nextValue === null && prevState.concurrentUserCount === null) {
                return prevState;
            }

            if (prevState.concurrentUserCount === nextValue) {
                return prevState;
            }

            return {...prevState, concurrentUserCount: nextValue ?? nextCount ?? undefined};
        });
    }, [onChange]);

    const onChangeContractID = useCallback((contractId: string) => {
        onChange(prevState => (prevState ? {...prevState, contractId} : null));
    }, [onChange]);

    const isSaveDisabled = useMemo(
        () => isInitialLicenseInformation || !!validationErrors.length,
        [validationErrors, isInitialLicenseInformation],
    );

    const isDeactivateLicense = useMemo(() => {
        if (license.startDate && license.endDate && !validationErrors.length) {
            return isBeforeToday(license.endDate);
        }
        return false;
    }, [license.endDate, license.startDate, isBeforeToday, validationErrors]);

    const limitedSeatsLicenses = [
        LicenseTypes.CU, LicenseTypes.SB, LicenseTypes.US, LicenseTypes.GP, LicenseTypes.UGP,
    ];

    const seatsRangeOptions = useMemo(() => {
        const hasSeatCount = license.concurrentUserCount != null;
        if (hasSeatCount) {
            return SEATS_RANGE_OPTIONS.filter(o => o.value !== null);
        }
        return SEATS_RANGE_OPTIONS;
    }, [license.concurrentUserCount]);

    useEffect(() => {
        if (SEATS_RANGE_LICENSES.includes(license.typeCode) && !license.concurrentUserCount) {
            onChangeSeats(100);
        }
    }, [onChangeSeats, license.concurrentUserCount, license.typeCode]);

    return (
        <>
            <DialogHeader onClose={onClose}>
                Edit License
            </DialogHeader>
            <DialogMain style={{padding: 24}}>
                {isDeactivateLicense ? (
                    <Message color="warning">
                        You won't be able to edit the expired license.
                        <br/>
                        Do you want to proceed?
                    </Message>
                ) : null}
                <Group direction="v">
                    <WidthItem width="100%">
                        <Label>License Period*</Label>
                        <Group direction="v">
                            <ValidatedControl name="license-start-date">
                                {(invalid: boolean) => (
                                    <DateRangeInput
                                        id="license-start-date-picker"
                                        label="Starts"
                                        width={300}
                                        invalid={invalid}
                                        clearable={true}
                                        startDate={license.startDate}
                                        endDate={license.endDate}
                                        selectRangeType="start"
                                        onChange={onChangeStartDate}
                                    />
                                )}
                            </ValidatedControl>
                            <ValidatedControl name="license-end-date">
                                {(invalid: boolean) => (
                                    <DateRangeInput
                                        id="license-end-date-picker"
                                        label={<>Ends&nbsp;&nbsp;</>}
                                        width={300}
                                        invalid={invalid}
                                        clearable={true}
                                        startDate={license.startDate}
                                        endDate={license.endDate}
                                        selectRangeType="end"
                                        onChange={onChangeEndDate}
                                    />
                                )}
                            </ValidatedControl>
                        </Group>
                    </WidthItem>
                    {limitedSeatsLicenses.includes(license.typeCode) && (
                        SEATS_RANGE_LICENSES.includes(license.typeCode) ? (
                            <WidthItem width={300}>
                                <Label id="seats-range-label" htmlFor="seats-range-combobox">
                                    Seat Range
                                </Label>
                                <ComboBox
                                    id="seats-range-combobox"
                                    value={getSeatsRangeValue(license.concurrentUserCount) ?? undefined}
                                    aria-labelledby="seats-range-label"
                                    onChange={onChangeSeats}
                                    placeholder="Select Seat Range">
                                    {seatsRangeOptions.map(({value, caption}) => (
                                        <ComboboxItem key={String(value)} value={value} caption={caption}/>
                                    ))}
                                </ComboBox>
                            </WidthItem>
                        ) : (
                            <CustomValidatedControl name="license-seats">
                                {error => (
                                    <NumberInput
                                        width="100%"
                                        label={(
                                            <span>
                                                Seats{license.typeCode === LicenseTypes.CU ? '*' : ''}
                                            </span>
                                        )}
                                        onBlur={onChangeSeats}
                                        onChange={onChangeSeats}
                                        value={license.concurrentUserCount ?? undefined}
                                        maxValue={9999}
                                        valid={error ? false : undefined}
                                        description={error}
                                        displayedValue={typeof license.concurrentUserCount === 'number'
                                            ? Integer.format(license.concurrentUserCount, locale)
                                            : ''}
                                    />
                                )}
                            </CustomValidatedControl>
                        )
                    )}
                    <FormInput
                        width={300}
                        label={
                            <Info type={{popover: CONTRACT_ID_EXPLANATION}}>Contract ID</Info>
                        }
                        value={license.contractId || ''}
                        maxLength={255}
                        onChange={onChangeContractID}
                    />
                </Group>
            </DialogMain>
            <DialogFooter>
                <Group>
                    <Button variant="Type.PRIMARY" busy={busy} disabled={isSaveDisabled} onClick={onSave}>
                        Save
                    </Button>
                    <Button variant="Type.SECONDARY" onClick={onClose}>
                        Cancel
                    </Button>
                </Group>
            </DialogFooter>
        </>
    );
};
